/**
 * GlobalCountdown Web Component
 *
 * A reusable countdown timer with flexible unit display options.
 * Supports smart mode (auto-select), all units, or custom combinations.
 * Can render as visual countdown or text-only format (localized).
 *
 * @example
 * <global-countdown
 *   data-target-date="2025-12-31T23:59:59-05:00"
 *   data-units="hours:minutes:seconds"
 *   data-zero-pad
 *   data-hide-zero-units>
 * </global-countdown>
 *
 * @example Text-only mode
 * <global-countdown
 *   data-target-date="2025-12-31T23:59:59-05:00"
 *   data-units="hours:minutes:seconds"
 *   data-text-only
 *   data-hide-zero-units>
 * </global-countdown>
 */
class GlobalCountdown extends HTMLElement {
  constructor() {
    super();

    this.targetDate = this.getAttribute('data-target-date');
    this.unitsMode = this.getAttribute('data-units') || 'smart';
    this.hideZero = this.hasAttribute('data-hide-zero');
    this.hideZeroUnits = this.hasAttribute('data-hide-zero-units');
    this.zeroPad = this.hasAttribute('data-zero-pad');
    this.textOnly = this.hasAttribute('data-text-only');

    this.msInSecond = 1000;
    this.msInMinute = 60 * 1000;
    this.msInHour = 60 * 60 * 1000;
    this.msInDay = 24 * 60 * 60 * 1000;
    this.msInWeek = 7 * 24 * 60 * 60 * 1000;

    this.animationFrameId = null;
    this.lastUpdateTime = 0;
    this.isTabVisible = true;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    this.targetDate = this.getAttribute('data-target-date');
    this.unitsMode = this.getAttribute('data-units') || 'smart';
    this.zeroPad = this.getAttribute('data-zero-pad') === 'true';
    this.hideZeroUnits = this.getAttribute('data-hide-zero-units') === 'true';
    this.hideOnZero = this.getAttribute('data-hide-zero') === 'true';
    this.textOnly = this.getAttribute('data-text-only') === 'true';

    this.endDate = Date.parse(this.targetDate);

    if (isNaN(this.endDate)) {
      console.error('GlobalCountdown: Invalid target date format', this.targetDate);
      return;
    }

    this.units = this.parseUnits(this.unitsMode);
    this.loadTranslations();
    this.setupAccessibility();
    this.setupVisibilityListener();
    this.startCountdown();
  }

  disconnectedCallback() {
    this.stopCountdown();
    this.cleanupListeners();
  }

  /**
   * Parse unit display configuration
   * @param {string} unitsString - 'smart', 'all', or colon-separated units
   * @returns {Object} Parsed unit configuration
   */
  parseUnits(unitsString) {
    if (unitsString === 'smart') {
      return { mode: 'smart' };
    }

    if (unitsString === 'all') {
      return { mode: 'all', units: ['weeks', 'days', 'hours', 'minutes', 'seconds'] };
    }

    const units = unitsString.split(':').map(u => u.trim());
    const validUnits = ['weeks', 'days', 'hours', 'minutes', 'seconds'];
    const filteredUnits = units.filter(u => validUnits.includes(u));

    if (filteredUnits.length === 0) {
      console.warn('GlobalCountdown: No valid units specified, defaulting to smart mode');
      return { mode: 'smart' };
    }

    return { mode: 'custom', units: filteredUnits };
  }

  /**
   * Load translation strings from data attributes
   */
  loadTranslations() {
    this.translations = {
      week: this.getAttribute('data-i18n-week') || 'Week',
      weeks: this.getAttribute('data-i18n-weeks') || 'Weeks',
      day: this.getAttribute('data-i18n-day') || 'Day',
      days: this.getAttribute('data-i18n-days') || 'Days',
      hour: this.getAttribute('data-i18n-hour') || 'Hour',
      hours: this.getAttribute('data-i18n-hours') || 'Hours',
      minute: this.getAttribute('data-i18n-minute') || 'Minute',
      minutes: this.getAttribute('data-i18n-minutes') || 'Minutes',
      second: this.getAttribute('data-i18n-second') || 'Second',
      seconds: this.getAttribute('data-i18n-seconds') || 'Seconds'
    };
  }

  /**
   * Setup ARIA attributes for accessibility
   */
  setupAccessibility() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'timer');
    }
    if (!this.hasAttribute('aria-live')) {
      this.setAttribute('aria-live', 'polite');
    }
    if (!this.hasAttribute('aria-atomic')) {
      this.setAttribute('aria-atomic', 'true');
    }
  }

  /**
   * Setup Page Visibility API listener
   */
  setupVisibilityListener() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Cleanup event listeners
   */
  cleanupListeners() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle tab visibility changes (pause/resume)
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.isTabVisible = false;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    } else {
      this.isTabVisible = true;
      this.startCountdown();
    }
  }

  /**
   * Start the countdown loop
   */
  startCountdown() {
    const updateLoop = (timestamp) => {
      if (!this.isTabVisible) return;

      const remaining = this.endDate - Date.now();

      if (remaining <= 0) {
        this.handleComplete();
        return;
      }

      if (remaining <= 10000 && this.getAttribute('aria-live') !== 'assertive') {
        this.setAttribute('aria-live', 'assertive');
        this.setAttribute('role', 'alert');
      }

      if (timestamp - this.lastUpdateTime >= 1000) {
        this.render(remaining);
        this.lastUpdateTime = timestamp;
      }

      this.animationFrameId = requestAnimationFrame(updateLoop);
    };

    this.animationFrameId = requestAnimationFrame(updateLoop);
  }

  /**
   * Stop the countdown loop
   */
  stopCountdown() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Calculate time units from milliseconds
   * @param {number} ms - Milliseconds remaining
   * @returns {Object} Time broken down into units
   */
  calculateTimeUnits(ms) {
    const weeks = Math.floor(ms / this.msInWeek);
    const days = Math.floor((ms % this.msInWeek) / this.msInDay);
    const hours = Math.floor((ms % this.msInDay) / this.msInHour);
    const minutes = Math.floor((ms % this.msInHour) / this.msInMinute);
    const seconds = Math.floor((ms % this.msInMinute) / this.msInSecond);

    return { weeks, days, hours, minutes, seconds };
  }

  /**
   * Render countdown display
   * @param {number} ms - Milliseconds remaining
   */
  render(ms) {
    const time = this.calculateTimeUnits(ms);

    if (this.units.mode === 'smart') {
      this.renderSmart(time);
    } else {
      this.renderUnits(time, this.units.units);
    }
  }

  /**
   * Render smart mode (show only most significant unit)
   * @param {Object} time - Time units object
   */
  renderSmart(time) {
    let value, unit;

    if (time.weeks > 0) {
      value = time.weeks;
      unit = 'week';
    } else if (time.days > 0) {
      value = time.days;
      unit = 'day';
    } else if (time.hours > 0) {
      value = time.hours;
      unit = 'hour';
    } else if (time.minutes > 0) {
      value = time.minutes;
      unit = 'minute';
    } else {
      value = time.seconds;
      unit = 'second';
    }

    this.updateDisplay({ [unit]: value });
  }

  /**
   * Render specific units
   * @param {Object} time - Time units object
   * @param {Array} requestedUnits - Units to display
   */
  renderUnits(time, requestedUnits) {
    const displayTime = {};
    const unitMap = {
      weeks: time.weeks,
      days: time.days,
      hours: time.hours,
      minutes: time.minutes,
      seconds: time.seconds
    };

    requestedUnits.forEach(unit => {
      const singularUnit = unit.replace(/s$/, '');
      displayTime[singularUnit] = unitMap[unit];
    });

    this.updateDisplay(displayTime);
  }

  /**
   * Update DOM with formatted time
   * @param {Object} timeObj - Object with unit keys and values
   */
  updateDisplay(timeObj) {
    if (this.textOnly) {
      this.renderTextOnly(timeObj);
    } else {
      this.renderVisualDisplay(timeObj);
    }
  }

  /**
   * Render text-only display (localized list format)
   * @param {Object} timeObj - Object with unit keys and values
   */
  renderTextOnly(timeObj) {
    const parts = [];

    Object.entries(timeObj).forEach(([unit, value]) => {
      if (this.hideZeroUnits && value === 0) {
        return;
      }

      const formattedValue = this.zeroPad && value < 10 ? `0${value}` : value;
      const translationKey = value === 1 ? unit : `${unit}s`;
      const label = this.translations[translationKey] || unit;

      parts.push(`${formattedValue} ${label}`);
    });

    if (parts.length === 0) {
      this.textContent = '';
      return;
    }

    const locale = document.documentElement.lang || 'en';

    if (typeof Intl !== 'undefined' && typeof Intl.ListFormat !== 'undefined') {
      try {
        const formatter = new Intl.ListFormat(locale, {
          style: 'long',
          type: 'conjunction'
        });
        this.textContent = formatter.format(parts);
      } catch (error) {
        console.warn('GlobalCountdown: Intl.ListFormat error, using fallback', error);
        this.textContent = parts.join(' ');
      }
    } else {
      this.textContent = parts.join(' ');
    }
  }

  /**
   * Render visual display (HTML structure)
   * @param {Object} timeObj - Object with unit keys and values
   */
  renderVisualDisplay(timeObj) {
    let html = '';

    Object.entries(timeObj).forEach(([unit, value]) => {
      if (this.hideZeroUnits && value === 0) {
        return;
      }

      const formattedValue = this.zeroPad && value < 10 ? `0${value}` : value;
      const translationKey = value === 1 ? unit : `${unit}s`;
      const label = this.translations[translationKey] || unit;

      const isSecond = unit === 'second';
      const ariaHidden = isSecond ? ' aria-hidden="true"' : '';

      html += `
        <span class="countdown__unit" data-unit="${unit}"${ariaHidden}>
          <strong class="countdown__value" data-value>${formattedValue}</strong>
          <span class="countdown__label" data-label>${label}</span>
        </span>
      `;
    });

    this.innerHTML = `<div class="countdown__units">${html}</div>`;
  }

  /**
   * Handle countdown completion
   */
  handleComplete() {
    this.stopCountdown();

    if (this.hideZero) {
      this.style.display = 'none';
    } else {
      const zeroTime = this.units.mode === 'smart'
        ? { second: 0 }
        : this.units.units.reduce((acc, unit) => {
            acc[unit.replace(/s$/, '')] = 0;
            return acc;
          }, {});

      this.updateDisplay(zeroTime);
    }

    this.dispatchEvent(new CustomEvent('countdown:complete', {
      bubbles: true,
      detail: { endDate: this.endDate }
    }));
  }
}

if (!customElements.get('global-countdown')) {
  customElements.define('global-countdown', GlobalCountdown);
}
